import { format } from 'date-fns/format'
import { intervalToDuration } from 'date-fns/intervalToDuration'
import { isSameDay } from 'date-fns/isSameDay'
import React from 'react'

import { chunkBy } from '@/shared/lib/fp'
import type { Language } from '@/shared/lib/i18n'
import { formatDateTime } from '@/shared/lib/time'
import type { NonEmptyArray } from '@/shared/lib/types'

import type { Message } from '../api'
import { isAllowedLink } from '../lib'
import type { ChannelItem, ChannelItemOfType } from '../model'

export function processItems(items: ChannelItem[]): ChannelItem[] {
    const chunks = chunkBy(items, (item, prev, chunks) => {
        // split messages by day
        if (
            item.type === 'message' &&
            prev.type === 'message' &&
            !isSameDay(item.payload.create_time, prev.payload.create_time)
        ) {
            chunks.push([
                {
                    type: 'day_divider',
                    payload: {
                        date: item.payload.create_time,
                    },
                },
            ])
            return false
        }

        // group messages by user
        if (item.type === 'message' && prev.type === 'message') {
            return item.payload.user_id === prev.payload.user_id
        }

        // group all other items by type
        return item.type === prev.type
    })

    return chunks.flatMap((chunk) => {
        // transform chunk of message events into message_group
        if (isChunkOfType(chunk, 'message')) {
            return chunk.map(
                (event, idx): ChannelItemOfType<'message_group'> => ({
                    type: 'message_group',
                    payload: {
                        user_id: event.payload.user_id,
                        message: event.payload,
                        header: idx === 0,
                        footer: idx === chunk.length - 1,
                    },
                })
            )
        }

        return chunk
    })
}

function isChunkOfType<T extends ChannelItem['type']>(
    chunk: NonEmptyArray<ChannelItem>,
    type: T
): chunk is NonEmptyArray<ChannelItemOfType<T>> {
    return chunk[0].type === type
}

export function isChannelItemsAreEqual(prev: ChannelItem, next: ChannelItem): boolean {
    if (prev.type === 'message_group' && next.type === 'message_group') {
        const prevItem = prev.payload
        const nextItem = next.payload
        return (
            prevItem.user_id === nextItem.user_id &&
            prevItem.header === nextItem.header &&
            prevItem.message === nextItem.message
        )
    }

    if (prev.type === 'day_divider' && next.type === 'day_divider') {
        return prev.payload.date === next.payload.date
    }

    return prev === next
}

export function singleMessageGroupItem(message: Message): ChannelItem {
    return {
        type: 'message_group',
        payload: {
            user_id: message.user_id,
            header: true,
            footer: true,
            message: message,
        },
    }
}

export function formatMessageTime(time: Date): string {
    return format(time, 'HH:mm')
}

export function formatMessageTimeFull(time: Date, lang: Language): string {
    return formatDateTime(time, lang, 'PPPpp')
}

export function formatMessage(message: string): (string | React.ReactElement<any>)[] {
    return message.split(' ').map((word, idx, { length }) => {
        const isLast = idx === length - 1
        const after = isLast ? '' : ' '

        if (isAllowedLink(word)) {
            // convert to link
            return (
                <a key={idx} href={word} target="_blank" rel="noopener noreferrer">
                    {word}
                    {after}
                </a>
            )
        }

        return `${word}${after}`
    })
}

export function formatTimer(seconds: number): string {
    const duration = intervalToDuration({ start: 0, end: Math.ceil(seconds) * 1000 })

    const formatted = [duration.hours || null, duration.minutes ?? 0, duration.seconds ?? 0]
        .flatMap((d) => (d === null ? [] : [d]))
        .map((d) => d.toString().padStart(2, '0'))
        .join(':')

    return formatted
}

export function useIsVisible(opts?: { keepVisible?: boolean }) {
    const ref = React.useRef(null)
    const isVisible = useIsVisibleRef(ref, opts)

    return { ref, isVisible }
}

export function useIsVisibleRef(ref: React.RefObject<HTMLElement | null>, opts?: { keepVisible?: boolean }) {
    const [isVisible, setIsVisible] = React.useState(false)

    React.useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            // update state when observer callback fires
            setIsVisible((isVisible) => {
                if (entry?.isIntersecting) {
                    return true
                }

                // Keep in view after it has been visible
                if (opts?.keepVisible && isVisible) {
                    return true
                }

                return false
            })
        })

        const target = ref.current
        if (target) {
            observer.observe(target)
        }

        // Clean up the observer when the component is unmounted
        return () => {
            if (target) {
                observer.unobserve(target as any)
            }
        }
    }, [opts?.keepVisible, ref]) // Re-run effect if ref changes

    return isVisible
}

export function scrollToMessage(messageId: string, behavior: 'smooth' | 'instant' = 'smooth') {
    const messageElement = getMessageElementById(messageId)
    if (!messageElement) {
        return
    }

    messageElement.scrollIntoView({ behavior, block: 'center' })

    const dataset = messageElement.dataset
    if (!dataset) {
        return
    }

    // highlight message
    dataset.highlight = 'true'

    // remove highlight after 1 second
    setTimeout(() => {
        const messageElement = getMessageElementById(messageId)
        const dataset = messageElement?.dataset
        if (!dataset) {
            return
        }

        // remove highlight
        delete dataset.highlight
    }, 1000)
}

export function useDelayedValue<T>(value: T, defaultValue: T, delay: number) {
    const [state, setState] = React.useState(defaultValue)
    React.useEffect(() => {
        const tid = setTimeout(() => {
            setState(value)
        }, delay)

        return () => {
            clearTimeout(tid)
        }
    }, [value, delay])

    return state
}

export function getMessageElementById(messageId: string): HTMLElement | null {
    return document.getElementById(getMessageElementId(messageId))
}

export function getMessageElementId(messageId: string): string {
    return `message-${messageId}`
}
